import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  inject,
  Input,
  OnDestroy,
  Optional,
  Self,
  ViewChild
} from '@angular/core';
import {ControlValueAccessor, FormControl, NgControl} from '@angular/forms';
import {MatFormFieldControl} from '@angular/material/form-field';
import {Subject} from 'rxjs';
import {coerceBooleanProperty} from '@angular/cdk/coercion';
import {DateAdapter, MAT_DATE_FORMATS} from '@angular/material/core';
import {MomentDateAdapter} from '@angular/material-moment-adapter';
import {MatDatepicker} from '@angular/material/datepicker';
import moment, {Moment} from 'moment';

export const MAT_DATETIME_FORMATS = {
  parse: {
    dateInput: ['YYYY-MM-DD HH:mm', 'LLL', 'LL'],
    timeInput: 'HH:mm',
  },
  display: {
    dateInput: 'LLL',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LLL',
    monthYearA11yLabel: 'MMMM YYYY',
    timeInput: 'HH:mm',
    timeOptionLabel: 'HH:mm',
  }
};

@Component({
  selector: 'app-mat-datetime',
  exportAs: 'matDatetime',
  standalone: false,
  templateUrl: './mat-datetime.component.html',
  styleUrl: './mat-datetime.component.scss',
  providers: [
    {provide: MatFormFieldControl, useExisting: MatDatetimeComponent},
    {provide: MAT_DATE_FORMATS, useValue: MAT_DATETIME_FORMATS}
  ]
})
export class MatDatetimeComponent implements MatFormFieldControl<Moment | null>, ControlValueAccessor, OnDestroy, AfterViewInit {
  static nextId = 0;

  private elementRef = inject(ElementRef);
  private cdr = inject(ChangeDetectorRef);

  @Input('aria-describedby') userAriaDescribedBy: string;
  @Input() minDate: Moment | null = null;
  @Input() maxDate: Moment | null = null;

  @ViewChild('picker', { static: false }) picker!: MatDatepicker<Moment>;

  @HostBinding() id = `app-mat-datetime-${MatDatetimeComponent.nextId++}`;

  dateControl = new FormControl<Moment | null>(null);
  timeValue: Moment | null = moment().hours(12).minutes(0).seconds(0).milliseconds(0);
  stateChanges = new Subject<void>();
  focused = false;
  controlType = 'app-mat-datetime';
  autofilled?: boolean;

  private _placeholder: string;
  private _required = false;
  private _disabled = false;
  private _touched = false;

  onChange = (_: any) => {};
  onTouched = () => {};

  constructor(@Optional() @Self() public ngControl: NgControl) {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }

  get empty() {
    return !this.dateControl.value;
  }

  @HostBinding('class.floating')
  get shouldLabelFloat() {
    return this.focused || !this.empty;
  }

  get errorState(): boolean {
    return this.ngControl && this.ngControl.errors !== null && this._touched;
  }

  @Input()
  get value(): Moment | null {
    return this.dateControl.value;
  }

  set value(date: Moment | null) {
    if (date && moment.isMoment(date)) {
      // Sync timeValue with the date's time
      this.timeValue = date.clone();
    }
    this.dateControl.setValue(date);
    this.stateChanges.next();
  }

  @Input()
  get placeholder() {
    return this._placeholder;
  }

  set placeholder(placeholder: string) {
    this._placeholder = placeholder;
    this.stateChanges.next();
  }

  @Input()
  get required() {
    return this._required;
  }

  set required(required: boolean) {
    this._required = coerceBooleanProperty(required);
    this.stateChanges.next();
  }

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }

  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);
    this._disabled ? this.dateControl.disable() : this.dateControl.enable();
    this.stateChanges.next();
  }

  ngAfterViewInit(): void {
    // Defer change detection to next tick to avoid ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => this.cdr.markForCheck(), 0);
  }

  ngOnDestroy(): void {
    this.stateChanges.complete();
  }

  togglePicker(): void {
    if (this.picker) {
      this.picker.open();
    }
  }

  setDescribedByIds(ids: string[]): void {
    const controlElement = this.elementRef.nativeElement.querySelector('input');
    if (controlElement) {
      controlElement.setAttribute('aria-describedby', ids.join(' '));
    }
  }

  onContainerClick(event: MouseEvent): void {
    if ((event.target as Element).tagName.toLowerCase() !== 'input') {
      this.elementRef.nativeElement.querySelector('input')?.focus();
    }
  }

  onFocusIn() {
    if (!this.focused) {
      this.focused = true;
      this.stateChanges.next();
    }
  }

  onFocusOut(event: FocusEvent) {
    if (!this.elementRef.nativeElement.contains(event.relatedTarget as Element)) {
      this._touched = true;
      this.focused = false;
      this.onTouched();
      this.stateChanges.next();
    }
  }

  onDateChange(date: Moment | null) {
    if (date) {
      // Preserve existing time when date changes, or use default (12:00) if not set
      const timeToUse = this.timeValue || moment().hours(12).minutes(0);
      date = date.clone()
        .hours(timeToUse.hours())
        .minutes(timeToUse.minutes())
        .seconds(0)
        .milliseconds(0);
      this.value = date;
    } else {
      this.value = null;
    }
    this.onChange(this.value);
  }

  onTimeChange(time: Moment | null) {
    if (time && this.dateControl.value) {
      const updatedDate = this.dateControl.value.clone()
        .hours(time.hours())
        .minutes(time.minutes())
        .seconds(0)
        .milliseconds(0);
      this.timeValue = time;
      this.value = updatedDate;
      this.onChange(updatedDate);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  writeValue(date: Moment | Date | string | null): void {
    if (date) {
      // Convert to Moment if needed
      this.value = moment.isMoment(date) ? date : moment(date);
    } else {
      this.value = null;
    }
  }
}
